import { type Badge, badgesRetrieve } from "@innoclouds/api";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useMainStore } from "#/store/main";
import { isBadgeActive, isVectorIcon } from "#/utils/badges";
import { addToCache } from "#/utils/cache";

const STORAGE_KEY = "badgesCache";
const EXPIRE_MAX_DELAY = 60 * 60 * 1000;
const EXPIRE_MIN_DELAY = 1_000;

type BadgesCache = { hash_sum: string; items: Badge[] };

const readCache = (): BadgesCache | null => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as BadgesCache;
		return Array.isArray(parsed?.items) ? parsed : null;
	} catch {
		return null;
	}
};

export const useBadgesStore = defineStore("badges", () => {
	const cached = readCache();

	const items = ref<Badge[]>(cached?.items ?? []);
	const hashSum = ref(cached?.hash_sum ?? "");
	const now = ref(Date.now());
	const vectorIcons = ref<Record<string, boolean>>({});

	let inFlight: Promise<void> | null = null;
	let loadedOnce = false;
	let expiryTimer: ReturnType<typeof setTimeout> | null = null;

	const nextExpiryAt = (from: number) => {
		let nearest: number | null = null;

		for (const badge of items.value) {
			for (const raw of [badge.active_from, badge.active_until]) {
				if (!raw) continue;
				const time = new Date(raw).getTime();
				if (!Number.isFinite(time) || time <= from) continue;
				if (nearest === null || time < nearest) nearest = time;
			}
		}

		return nearest;
	};

	const scheduleExpiry = () => {
		if (expiryTimer) clearTimeout(expiryTimer);
		expiryTimer = null;
		if (typeof window === "undefined") return;

		const current = Date.now();
		const nearest = nextExpiryAt(current);
		if (nearest === null) return;

		const delay = Math.min(Math.max(nearest - current + EXPIRE_MIN_DELAY, EXPIRE_MIN_DELAY), EXPIRE_MAX_DELAY);
		expiryTimer = setTimeout(() => {
			now.value = Date.now();
			scheduleExpiry();
		}, delay);
	};

	const stopExpiryTimer = () => {
		if (expiryTimer) clearTimeout(expiryTimer);
		expiryTimer = null;
	};

	const byKey = computed(() => new Map(items.value.map((badge) => [badge.key, badge])));

	const load = (pointId?: string) => {
		const orgUnitId = pointId || useMainStore().config_lib?.pointId;
		if (!orgUnitId) return Promise.resolve();
		if (inFlight) return inFlight;

		inFlight = (async () => {
			try {
				const data = await badgesRetrieve(orgUnitId);
				items.value = data?.items ?? [];
				hashSum.value = data?.hash_sum ?? "";
				loadedOnce = true;
				localStorage.setItem(STORAGE_KEY, JSON.stringify({ hash_sum: hashSum.value, items: items.value }));
				now.value = Date.now();
				scheduleExpiry();
				await Promise.allSettled(items.value.filter((badge) => badge.icon).map((badge) => addToCache(badge.icon)));
			} catch (error) {
				console.warn("[badges] load failed:", error);
			} finally {
				inFlight = null;
			}
		})();

		return inFlight;
	};

	const ensureLoaded = (pointId?: string) => {
		if (loadedOnce) return Promise.resolve();
		return load(pointId);
	};

	const iconUrl = (badge: Badge) => badge.icon || null;

	const isVectorBadgeIcon = (badge: Badge) => vectorIcons.value[badge.key] ?? isVectorIcon(badge.icon);

	const resolve = (keys?: string[] | null, limit?: number | null) => {
		if (!keys?.length || !items.value.length) return [];

		const map = byKey.value;
		const list = keys.map((key) => map.get(key)).filter((badge): badge is Badge => !!badge && isBadgeActive(badge, now.value));

		list.sort((first, second) => (first.priority ?? 0) - (second.priority ?? 0) || first.key.localeCompare(second.key));

		return limit && limit > 0 ? list.slice(0, limit) : list;
	};

	if (items.value.length) scheduleExpiry();

	return {
		items,
		hashSum,
		byKey,
		load,
		ensureLoaded,
		iconUrl,
		isVectorBadgeIcon,
		resolve,
		stopExpiryTimer,
	};
});
